/**
 * ProofUploadModal Component
 * Modal for uploading, viewing, and removing power proof documents (images or PDF)
 * Tailwind version using OathUI components
 */

import { useState, useEffect, useRef } from 'react';
import {
  CloudArrowUpIcon,
  TrashIcon,
  XMarkIcon,
  MagnifyingGlassPlusIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { OathModal, OathButton, OathSpinner, OathAlert } from '../../../shared/OathUI';
import { toast } from 'react-toastify';
import {
  uploadProofImage,
  getProofImage,
  deleteProofImage
} from '../../../../services/oathPowerReportService';
import { PROOF_FILE_CONFIG } from '../utils/constants';

const isPdfFile = (file) => file?.type === 'application/pdf';
const isPdfContentType = (ct) => ct?.startsWith('application/pdf');

// Convert a base64 payload to an object URL — data: URIs are unreliable in PDF iframes
const base64ToObjectUrl = (base64, contentType) => {
  const byteChars = atob(base64);
  const bytes = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) {
    bytes[i] = byteChars.charCodeAt(i);
  }
  return URL.createObjectURL(new Blob([bytes], { type: contentType }));
};

const ProofUploadModal = ({ isOpen, onClose, power, onProofUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState(null);
  const [proofImage, setProofImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [fullViewOpen, setFullViewOpen] = useState(false);
  const fileInputRef = useRef(null);

  // Load existing proof when modal opens
  useEffect(() => {
    if (isOpen && power?.powerReportPowerId) {
      loadExistingProof();
    } else {
      // Reset state when modal closes
      setProofImage(null);
      setSelectedFile(null);
      setPreviewUrl(null);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, power?.powerReportPowerId]);

  // Clean up preview URL when component unmounts or file changes
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Clean up the existing-proof object URL when it changes or the component unmounts
  useEffect(() => {
    const url = proofImage?.url;
    return () => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    };
  }, [proofImage]);

  const loadExistingProof = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await getProofImage(power.powerReportPowerId);

      if (result.success && result.data?.hasProof && result.data?.proofBase64) {
        const contentType = result.data.contentType || 'image/jpeg';
        setProofImage({
          url: base64ToObjectUrl(result.data.proofBase64, contentType),
          contentType,
          fileName: result.data.fileName
        });
      } else {
        setProofImage(null);
      }
    } catch (err) {
      console.error('Error loading proof:', err);
      setProofImage(null);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!PROOF_FILE_CONFIG.ACCEPTED_TYPES.includes(file.type)) {
        setError(`"${file.name}" is not an accepted file type. Accepted: JPG, PNG, GIF, PDF.`);
        return;
      }

      // Validate file size
      if (file.size > PROOF_FILE_CONFIG.MAX_SIZE_BYTES) {
        setError(`File size must be less than ${PROOF_FILE_CONFIG.MAX_SIZE_MB}MB`);
        return;
      }

      setSelectedFile(file);
      setError(null);

      // Create preview URL
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      setError(null);

      const result = await uploadProofImage(power.powerReportPowerId, selectedFile);

      if (result.success) {
        toast.success('Proof uploaded successfully');
        setSelectedFile(null);
        setPreviewUrl(null);
        onProofUpdated && onProofUpdated();
        onClose();
      } else {
        setError(result.error || 'Failed to upload proof');
      }
    } catch (err) {
      console.error('Error uploading proof:', err);
      setError('Failed to upload proof: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    try {
      setRemoving(true);
      setError(null);

      const result = await deleteProofImage(power.powerReportPowerId);

      if (result.success) {
        toast.success('Proof removed successfully');
        setProofImage(null);
        onProofUpdated && onProofUpdated();
        onClose();
      } else {
        setError(result.error || 'Failed to remove proof');
      }
    } catch (err) {
      console.error('Error removing proof:', err);
      setError('Failed to remove proof: ' + err.message);
    } finally {
      setRemoving(false);
    }
  };

  const imageSrc = previewUrl || proofImage?.url || null;
  const showPdf = selectedFile ? isPdfFile(selectedFile) : isPdfContentType(proofImage?.contentType);
  const proofFileName = selectedFile?.name || proofImage?.fileName || 'PDF Document';
  const hasExistingProof = proofImage?.url && !selectedFile;
  const isProcessing = uploading || removing;

  return (
    <>
      <OathModal
        isOpen={isOpen}
        onClose={isProcessing ? undefined : onClose}
        title="Power Proof Upload"
        size="md"
        footer={
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto sm:ml-auto">
            <OathButton
              variant="outline"
              onClick={onClose}
              disabled={isProcessing}
              className="w-full sm:w-auto"
            >
              Cancel
            </OathButton>

            {hasExistingProof && (
              <OathButton
                variant="danger"
                onClick={handleRemove}
                disabled={isProcessing}
                icon={removing ? null : TrashIcon}
                className="w-full sm:w-auto"
              >
                {removing ? (
                  <span className="flex items-center gap-2">
                    <OathSpinner size="sm" />
                    Removing...
                  </span>
                ) : (
                  'Remove Proof'
                )}
              </OathButton>
            )}

            <OathButton
              onClick={handleUpload}
              disabled={!selectedFile || isProcessing}
              icon={uploading ? null : CloudArrowUpIcon}
              className="w-full sm:w-auto"
            >
              {uploading ? (
                <span className="flex items-center gap-2">
                  <OathSpinner size="sm" />
                  Uploading...
                </span>
              ) : (
                'Upload'
              )}
            </OathButton>
          </div>
        }
      >
        <div className="space-y-4">
          {/* Power Number Display */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
              Power Number
            </p>
            <p className="text-lg font-mono font-bold text-gray-900 dark:text-white">
              {power?.powerNumber || 'N/A'}
            </p>
          </div>

          {/* Error Display */}
          {error && (
            <OathAlert variant="error" onClose={() => setError(null)}>
              {error}
            </OathAlert>
          )}

          {/* Loading State */}
          {loading ? (
            <div className="flex justify-center py-8">
              <OathSpinner size="lg" />
            </div>
          ) : (
            <>
              {/* File Input */}
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  {hasExistingProof ? 'Replace Proof' : 'Choose Proof File'}
                </p>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept={PROOF_FILE_CONFIG.ACCEPTED_EXTENSIONS}
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isProcessing}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl text-gray-600 dark:text-gray-400 hover:border-primary hover:text-primary dark:hover:border-primary-light dark:hover:text-primary-light transition-colors disabled:opacity-50"
                >
                  <CloudArrowUpIcon className="h-5 w-5" />
                  <span>{selectedFile ? selectedFile.name : 'Select File'}</span>
                </button>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Accepted formats: JPG, PNG, GIF, PDF (Max {PROOF_FILE_CONFIG.MAX_SIZE_MB}MB)
                </p>
              </div>

              {/* Proof Preview */}
              {imageSrc && (
                <div className="relative border border-gray-200 dark:border-gray-700 rounded-xl p-3 bg-gray-50 dark:bg-gray-800">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    {selectedFile ? 'Preview (New File)' : 'Current Proof'}
                  </p>
                  {showPdf ? (
                    <div
                      className="py-6 text-center cursor-pointer group hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      onClick={() => setFullViewOpen(true)}
                    >
                      <DocumentTextIcon className="h-12 w-12 text-red-500 mx-auto mb-2" />
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{proofFileName}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Click to view full size</p>
                    </div>
                  ) : (
                    <div className="flex justify-center">
                      <img
                        src={imageSrc}
                        alt="Power proof"
                        className="max-w-full max-h-64 object-contain rounded-lg cursor-pointer"
                        onClick={() => setFullViewOpen(true)}
                      />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => setFullViewOpen(true)}
                    className="absolute top-3 right-3 p-2 bg-white dark:bg-gray-700 rounded-full shadow-md hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                    title="View full size"
                  >
                    <MagnifyingGlassPlusIcon className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                  </button>
                </div>
              )}

              {/* No Proof State */}
              {!imageSrc && !loading && (
                <div className="border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl p-8 text-center">
                  <DocumentTextIcon className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No proof uploaded yet
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </OathModal>

      {/* Full View Modal */}
      {fullViewOpen && imageSrc && (
        <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4">
          <button
            type="button"
            onClick={() => setFullViewOpen(false)}
            className="absolute top-4 right-4 p-2 text-white hover:bg-white/20 rounded-full transition-colors"
          >
            <XMarkIcon className="h-8 w-8" />
          </button>
          {showPdf ? (
            <iframe
              src={imageSrc}
              title="Power proof full view"
              className="w-full h-full max-w-5xl rounded-lg bg-white"
            />
          ) : (
            <img
              src={imageSrc}
              alt="Power proof full view"
              className="max-w-full max-h-full object-contain"
            />
          )}
        </div>
      )}
    </>
  );
};

export default ProofUploadModal;
